/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import getPillarLocalDevelopment from '@jira/ssr__global-build-context/src/getPillarLocalDevelopment.tsx';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { FeatureGateEnvironment } from '@atlaskit/feature-gate-js-client/types';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { IS_MODERN_SSR } from '@jira/platform__conditional-ssr/src/constants.tsx';
import { getCustomAttributes, getGateValues } from './utils.tsx';
import { setUnreportedStatsigInitialisationError } from './unreported-statsig-initialisation-error.tsx';

type WindowFlagVars = {
	_statsigValuesReady: boolean | undefined;
};

// This script is part of early scripts, where analytics clients are too heavy to be included.
// Therefore, we just record the error in a global variable. Downstream code will report this.
const captureStatsigInitError = (error: unknown) => {
	setUnreportedStatsigInitialisationError(
		new Error('Statsig initialisation failed', {
			cause: error,
		}),
	);
};

const initIfNeeded = () => {
	if (!FeatureGates.initializeCalled()) {
		try {
			const gateValues = getGateValues();

			const initPromise = FeatureGates.initializeFromValues(
				{
					sdkKey: 'client-7hFqlBhHaY5b2zA7LaaHDy1QKH7yZigbikmrwNpCuRy',
					// this environment variable is defined in webpack.config.js
					environment:
						getMeta('ajs-environment') === 'prod'
							? FeatureGateEnvironment.Production
							: FeatureGateEnvironment.Staging,
					targetApp: 'jira_web',
					// Enable exposure firing from server side
					...(__SERVER__ && !getPillarLocalDevelopment() ? { ignoreWindowUndefined: true } : null),
				},
				{
					// These getMeta functions should only be called once due to performance implications
					// Ideally we should get these passed through from react which memoizes this call already
					atlassianAccountId: getMeta('ajs-atlassian-account-id') || undefined,
					tenantId: getMeta('ajs-cloud-id') ?? undefined,
					atlassianOrgId: getMeta('ajs-org-id') ?? undefined,
				},
				getCustomAttributes(),
				gateValues,
			);

			initPromise.catch(captureStatsigInitError);
		} catch (error: unknown) {
			captureStatsigInitError(error);

			if (error instanceof Error) {
				// eslint-disable-next-line no-console
				console.error('statsig.client.initialization', error.message);
			}
		}
	}
};

/**
 * UNSAFE DO NOT USE
 * To be used internally by feature gate packages only
 * Initialization for vendorless scripts when values
 * have not been bootstrapped
 */
export const UNSAFE_initIfNeeded = (): void => {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const globalVar = (typeof window !== 'undefined'
		? window
		: globalThis) as unknown as WindowFlagVars;

	// If on entry-fs, we need to check if the statsig values are ready
	// Mainly avoid the statsig initialisation is trying to use a default value
	// which is an empty object.
	if (IS_MODERN_SSR && globalVar._statsigValuesReady !== true) {
		return;
	}

	initIfNeeded();
};
